import React, { FC } from 'react';
import { Template } from './components/Template/Template';
import s from './TemplatesGrid.scss';
import { templatesGridDataHooks } from './TemplatesGrid.dataHooks';
import { isBlankTemplate } from '../../domain/entities/template';
import { TemplatesCarousel } from '../TemplatesCarousel/TemplatesCarousel';
import cs from 'classnames';
import { ErrorModal } from '../ErrorModal/ErrorModal';
import { TemplatesPageHeading } from '../TemplatesPage/TemplatesPageHeading/TemplatesPageHeading';
import { useTranslation } from '@wix/wix-i18n-config';
import { useSearch } from '../../hooks/useSearch';
import { useSelectedCategory } from '../../hooks/useSelectedCategory';
import { useTemplates } from '../../contexts/TemplatesContext';
import { useExperiments } from '../../contexts/ExperimentsContext';
import { useCurrentPage } from '../../hooks/useCurrentPage';
import { MarketplaceTemplatesCarousel } from '../MarketplaceTemplatesCarousel/MarketplaceTemplatesCarousel';
import { CATEGORY_SLUGS_TO_MARKETPLACE_TEMPLATE_IDS } from '../../config';

export interface TemplatesGridProps {
  heading: string;
}

export const TemplatesGrid: FC<TemplatesGridProps> = ({ heading }) => {
  const { templates, error, loading } = useTemplates();
  const { criteria } = useSearch();
  const numOfTemplatesInViewport = 6;
  const selectedCategory = useSelectedCategory();
  const { t } = useTranslation();
  const { is } = useExperiments();
  const page = useCurrentPage();

  const isEmptyResult = !error && !loading && templates.length === 0;
  const isEmptyCategory = isEmptyResult && selectedCategory && !criteria;

  const showInspirationTemplates =
    selectedCategory?.slug === 'all-templates' && page === 1;

  const showMarketplaceTemplates =
    is('specs.funnel.StudioTemplatesMarketplaceGallery', 'true') &&
    selectedCategory?.slug &&
    !!CATEGORY_SLUGS_TO_MARKETPLACE_TEMPLATE_IDS[selectedCategory.slug] &&
    page === 1;

  const pantoneSubtitle =
    is('specs.funnel.PantoneWixIntegration', 'true') &&
    selectedCategory?.slug === 'wix-studio-x-pantone'
      ? t('app.new.title.search.pantone.subtitle')
      : undefined;

  return (
    <div>
      <div className={s.headingWrapper}>
        <TemplatesPageHeading
          heading={
            isEmptyResult
              ? isEmptyCategory
                ? t('app.new.title.notResults.technicalIssue.title')
                : t('app.new.title.search.notFound.title', { criteria })
              : heading
          }
          subHeading={
            isEmptyResult
              ? t('app.new.title.search.notFound.subtitle')
              : pantoneSubtitle || undefined
          }
        />
      </div>
      <div
        data-hook={templatesGridDataHooks.templatesGrid()}
        className={cs(s.templates)}
      >
        {!isEmptyResult &&
          templates?.map((template, index) => (
            <Template
              key={template.templateId}
              template={template}
              isLazyLoad={index > numOfTemplatesInViewport}
              index={index}
              className={cs({
                [s.desktopOnly]: isBlankTemplate(template),
              })}
            />
          ))}
        {showMarketplaceTemplates && (
          <div
            className={s.marketplaceCarousel}
            data-hook={templatesGridDataHooks.marketplaceTemplatesCarousel()}
          >
            <MarketplaceTemplatesCarousel />
          </div>
        )}
        {showInspirationTemplates && (
          <div
            className={s.inspirationalCarousel}
            data-hook={templatesGridDataHooks.inspirationalTemplatesCarousel()}
          >
            <TemplatesCarousel />
          </div>
        )}
      </div>
      {error && <ErrorModal />}
    </div>
  );
};
